/**
 * What this tab knows about the SESSION (ADR-0302): which account the shared
 * cookie is on, and whether another tab signed out or signed in as someone
 * else.
 *
 * Not React state. In a tab left behind by a switch elsewhere, `activeAccount`
 * still says the old account; this store is updated from every source that
 * learns the truth, synchronously:
 * - every auth response the tab receives (`/me`, `/refresh`, a switch, a sign-in),
 * - the `sessionAccount` of a 409 `account_mismatch`,
 * - a message from another tab.
 *
 * The mismatch gate compares the tab's URL account with this, and the
 * navigation wrappers prefix with it on pages that carry no account.
 */

export type SessionNotice = "other-user" | "signed-out";

export type SessionState = {
  /** The account the session cookie is on, when known. */
  accountPublicId: string | null;
  /** Set when another tab changed WHO is signed in; clears on the next auth response. */
  notice: SessionNotice | null;
};

type Listener = () => void;

let state: SessionState = { accountPublicId: null, notice: null };
const listeners = new Set<Listener>();

function emit(next: SessionState): void {
  if (next.accountPublicId === state.accountPublicId && next.notice === state.notice) return;
  state = next;
  for (const listener of listeners) listener();
}

export function getSessionState(): SessionState {
  return state;
}

export function getSessionAccount(): string | null {
  return state.accountPublicId;
}

/** An auth response this tab received: the authoritative answer, notice cleared. */
export function setSessionAccountFromAuth(accountPublicId: string | null): void {
  emit({ accountPublicId, notice: null });
}

/** A second-hand report (a 409 body, another tab's message). Keeps a notice. */
export function reportSessionAccount(accountPublicId: string): void {
  emit({ accountPublicId, notice: state.notice });
}

export function reportSessionNotice(notice: SessionNotice): void {
  emit({ accountPublicId: state.accountPublicId, notice });
}

export function subscribeSessionState(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test seam. */
export function resetSessionStateForTests(): void {
  state = { accountPublicId: null, notice: null };
}
